import Highcharts from 'highcharts';
import { ICONS } from '../../../../../utils';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../../utils/custom-renderer/hyperlink-cell-renderer';
import { QuadrantGrid } from './quadrant-grid';
import { getFillColorFromProjectStatus, getFormattedNumber } from '../../../../../utils/formatting';
import { getTechnologyIconName } from '../../../../../styles';
import { CUSTOM_HEADER_TOOLTIPS, headerWithTooltipColParams } from '../../../../../utils/grid';

export enum COLUMN_FIELDS {
  PROJECT_NAME = 'PROJECT_NAME',
  UNIQUE_ID = 'UNIQUE_ID',
  REFERENCED_EXPECTED_SETTLEMENT = 'REFERENCED_EXPECTED_SETTLEMENT',
  REFERENCED_IMPLIED_CARBON_PRICE = 'REFERENCED_IMPLIED_CARBON_PRICE',
  REFERENCED_LME = 'REFERENCED_LME',
  ISO = 'ISO',
}

export function VPPA_COLUMNS(context: QuadrantGrid) {
  return [
    {
      field: COLUMN_FIELDS.PROJECT_NAME,
      headerName: 'Project',
      suppressMenu: true,
      flex: 1,
      sortable: false,
      cellStyle: {
        color: '#a9ff78',
      },
      valueGetter: (props) => {
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

        iconList.push({
          icon: ICONS.CIRCLE,
          width: '14px',
          height: '14px',
          fill: Highcharts.getOptions().colors[props.node.rowIndex],
        });

        iconList.push({
          icon: getTechnologyIconName(props.data.TECHNOLOGY),
          width: '12px',
          height: '13px',
          fill: getFillColorFromProjectStatus(props.data.PROJECT_OPERATIONAL_STATUS),
        });

        return JSON.stringify({
          NAME: props.data.PROJECT_NAME,
          PROJECT_ID: props.data.PROJECT_ID,
          ICON_LIST: iconList,
        });
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const obj = JSON.parse(params.value);
        if (!obj.NAME) {
          const hyperlinkParams: IGridHyperlink = {
            value: null,
            id: null,
            vppaId: null,
            iconList: [],
          };
          return hyperlinkParams;
        }

        const hyperlinkParams: IGridHyperlink = {
          value: obj.NAME,
          id: obj.PROJECT_ID,
          vppaId: null,
          iconList: obj.ICON_LIST,
        };
        return hyperlinkParams;
      },
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.ICON_LEGEND),
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique ID',
      filter: 'agTextColumnFilter',
      flex: 1,
      valueGetter: (props) => {
        return JSON.stringify({
          NAME: props.data.UNIQUE_ID,
          PROJECT_ID: props.data.PROJECT_ID,
        });
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj.NAME,
          id: obj.PROJECT_ID,
          vppaId: params.data?.WATCHLIST_ENTITY_ID,
          iconList: [],
        };
        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.REFERENCED_EXPECTED_SETTLEMENT,
      headerName: 'Expected Settlement ($/MWh)',
      filter: 'agTextColumnFilter',
      flex: 1,
      filterValueGetter: (props) =>
        getFormattedNumber(props.data.REFERENCED_EXPECTED_SETTLEMENT, '', 2),
      valueFormatter: (props) =>
        getFormattedNumber(props.data.REFERENCED_EXPECTED_SETTLEMENT, '', 2, '$'),
    },
    {
      field: COLUMN_FIELDS.REFERENCED_IMPLIED_CARBON_PRICE,
      headerName: 'Implied Carbon Price ($/MT CO₂)',
      filter: 'agTextColumnFilter',
      flex: 1,
      filterValueGetter: (props) =>
        getFormattedNumber(props.data.REFERENCED_IMPLIED_CARBON_PRICE, '', 2),
      valueFormatter: (props) =>
        getFormattedNumber(props.data.REFERENCED_IMPLIED_CARBON_PRICE, '', 2, '$'),
    },
    {
      field: COLUMN_FIELDS.REFERENCED_LME,
      headerName: 'LME (MT CO₂/MWh)',
      filter: 'agTextColumnFilter',
      flex: 1,
      filterValueGetter: (props) => getFormattedNumber(props.data.REFERENCED_LME, '', 2),
      valueFormatter: (props) => getFormattedNumber(props.data.REFERENCED_LME, '', 2),
    },
  ];
}
